import { memo } from "react";

import type { RemovePlayerHandler } from "@/features/player-kanban/types/types";
import PlayerListSection from "@/features/player-kanban/components/organisms/PlayerListSection";
import SidebarRemovalZone from "@/features/player-kanban/components/molecules/SidebarRemovalZone";
import PlayerSourceSelector, {
  PlayerSource,
} from "@/features/player-kanban/components/molecules/PlayerSourceSelector";
import SearchAndControls from "@/features/player-kanban/components/molecules/SearchAndControls";
import WatchlistSection from "@/features/player-kanban/components/organisms/WatchlistSection";

import { Box } from "@/components/atoms/Box";
import { Stack } from "@/components/atoms/Stack";
import { Spinner } from "@/components/atoms/Spinner";
import { Text } from "@/components/atoms/Text";

import type { PlayerKanbanPlayerFragmentFragment } from "@/generated/graphql";
import { TEST_ID_PLAYER_KANBAN } from "e2e/testId";

/**
 *
 */
interface Stats {
  totalPlayers: number;
  avgGpr: string;
  avgContract: string;
  activeCount: number;
  onKanban: number;
}

/**
 *
 */
interface Watchlist {
  id: string;
  name: string;
}

/**
 * Props for PlayerSidebarView component
 */
interface PlayerSidebarViewProps {
  /**
   * Statistical summary for the sidebar
   */
  stats: Stats;
  /**
   * Array of player objects to display in the sidebar list
   */
  players: PlayerKanbanPlayerFragmentFragment[];
  /**
   * Current search query text for filtering players
   */
  searchQuery: string;
  /**
   * Label indicating the current sort field
   */
  sortLabel: string;
  /**
   * The currently selected player data source
   */
  playerSource: PlayerSource;
  /**
   * The currently selected watchlist object
   */
  selectedWatchlist: Watchlist | null;
  /**
   * Array of available watchlists for selection
   */
  watchlists: Watchlist[];
  /**
   * Record of player IDs to their current kanban phase information
   */
  playersOnKanban: Record<string, { phaseId: string; phaseName: string }>;
  /**
   * Whether any item is currently being dragged globally
   */
  isGloballyDragging?: boolean;
  /**
   * The source of the current drag operation
   */
  dragSource?: "sidebar" | "kanban" | null;
  /**
   * Whether to display the removal drop zone
   */
  showRemovalZone: boolean;
  /**
   * Style classes for the sidebar container
   */
  sidebarClassName: string;
  /**
   * Ref setter function for the droppable removal zone
   */
  setNodeRef: (node: HTMLElement | null) => void;
  /**
   * Whether the My Roster feature is enabled
   */
  isMyRosterEnabled: boolean;
  /**
   * Whether watchlist data is currently being fetched
   */
  isLoadingWatchlists?: boolean;
  /**
   * Error message if watchlist loading failed
   */
  watchlistError?: string | null;
  /**
   * Whether player data is currently being fetched
   */
  isLoadingPlayers?: boolean;
  /**
   * Error message if player loading failed
   */
  playersError?: string | null;
  /**
   * Callback invoked when the search query changes
   */
  onSearchChange: (query: string) => void;
  /**
   * Callback invoked when the sort option changes
   */
  onSortChange: () => void;
  /**
   * Callback invoked when a player card is clicked
   */
  onPlayerClick: (playerId: string) => void;
  /**
   * Callback invoked when the player source changes
   */
  onSourceChange: (source: PlayerSource) => void;
  /**
   * Callback invoked when a different watchlist is selected
   */
  onWatchlistChange: (watchlistId: string) => void;
  /**
   * Callback invoked when any drag operation starts
   */
  onGlobalDragStart?: (dragData?: unknown, event?: unknown) => void;
  /**
   * Callback invoked when any drag operation ends
   */
  onGlobalDragEnd?: () => void;
  /**
   * Callback invoked when a player is dropped in the removal zone
   */
  onRemovePlayerFromBoard?: RemovePlayerHandler;
  /**
   * Callback invoked to trigger automatic scrolling during drag operations
   */
  onAutoScroll?: (dragX: number) => void;
  /**
   * Callback invoked when the user scrolls near the bottom of the player list to load more
   */
  onLoadMore?: () => void;
}

/**
 * View component for rendering the player sidebar UI with source selector, watchlist dropdown, stats, search, and player list.
 * @param root0 - The component props
 * @param root0.stats - Statistical summary including total players, average GPR, average contract, active count, and players on kanban
 * @param root0.players - Array of player objects to display in the sidebar list
 * @param root0.searchQuery - Current search query text for filtering players
 * @param root0.sortLabel - Label indicating the current sort field
 * @param root0.playerSource - The currently selected player data source (watchlist or roster)
 * @param root0.selectedWatchlist - The currently selected watchlist object with id, name, and player count
 * @param root0.watchlists - Array of available watchlists for selection
 * @param root0.playersOnKanban - Record of player IDs to their current kanban phase information
 * @param root0.isGloballyDragging - Whether any item is currently being dragged globally
 * @param root0.dragSource - The source of the current drag operation (sidebar, kanban, or null)
 * @param root0.showRemovalZone - Whether to display the removal drop zone for removing players from the board
 * @param root0.sidebarClassName - Style classes for the sidebar container including background, border, and dimensions
 * @param root0.setNodeRef - Ref setter function for the droppable removal zone
 * @param root0.isMyRosterEnabled - Whether the My Roster feature is enabled via feature flag
 * @param root0.isLoadingWatchlists - Whether watchlist data is currently being fetched
 * @param root0.watchlistError - Error message to display if watchlist loading failed
 * @param root0.isLoadingPlayers - Whether player data is currently being fetched
 * @param root0.playersError - Error message to display if player loading failed
 * @param root0.onSearchChange - Callback invoked when the search query changes
 * @param root0.onSortChange - Callback invoked when the sort option changes
 * @param root0.onPlayerClick - Callback invoked when a player card is clicked
 * @param root0.onSourceChange - Callback invoked when the player source (watchlist/roster) changes
 * @param root0.onWatchlistChange - Callback invoked when a different watchlist is selected
 * @param root0.onGlobalDragStart - Callback invoked when any drag operation starts (typed as unknown for cross-component compatibility)
 * @param root0.onGlobalDragEnd - Callback invoked when any drag operation ends
 * @param root0.onRemovePlayerFromBoard - Callback invoked when a player is dropped in the removal zone
 * @param root0.onAutoScroll - Callback invoked to trigger automatic scrolling during drag operations
 * @param root0.onLoadMore - Callback invoked when the user scrolls near the bottom to load more players
 */
const PlayerSidebarView = ({
  stats,
  players,
  searchQuery,
  sortLabel: _sortLabel,
  playerSource,
  selectedWatchlist,
  watchlists,
  playersOnKanban,
  isGloballyDragging: _isGloballyDragging = false,
  dragSource: _dragSource = null,
  showRemovalZone,
  sidebarClassName,
  setNodeRef,
  isMyRosterEnabled,
  isLoadingWatchlists: _isLoadingWatchlists = false,
  watchlistError: _watchlistError,
  isLoadingPlayers = false,
  playersError,
  onSearchChange,
  onSortChange,
  onPlayerClick,
  onSourceChange,
  onWatchlistChange,
  onGlobalDragStart,
  onGlobalDragEnd,
  onRemovePlayerFromBoard: _onRemovePlayerFromBoard,
  onAutoScroll,
  onLoadMore,
}: PlayerSidebarViewProps) => (
  <Box
    ref={setNodeRef}
    {...{ "data-testid": TEST_ID_PLAYER_KANBAN.PLAYER_SIDEBAR }}
    className={sidebarClassName}
  >
    {showRemovalZone ? (
      // Show only removal zone when dragging from kanban. SE-5490 extracted
      // this into a molecule so the pipeline surface's add-players panel
      // presents the identical target rather than losing drag-to-remove.
      <SidebarRemovalZone />
    ) : (
      // Show normal sidebar content when not dragging from kanban
      <Stack className="h-full overflow-visible bg-transparent">
        {/* Header Section with Source Selector and Watchlist */}
        <Stack className="gap-2 px-3 pb-2 pt-3">
          {/* Player Source Selector */}
          <PlayerSourceSelector
            selectedSource={playerSource}
            onSourceChange={onSourceChange}
          />

          {/* Watchlist Dropdown - Only show when source is watchlist */}
          {playerSource === "watchlist" && (
            <WatchlistSection
              selectedWatchlist={selectedWatchlist}
              watchlists={watchlists}
              onWatchlistChange={onWatchlistChange}
            />
          )}

          {/* Roster Header - Show when source is roster and feature is enabled */}
          {isMyRosterEnabled && playerSource === "roster" && (
            <Text variant="body-strong" className="text-content-muted">
              Organization Roster
            </Text>
          )}

          {/* Compact Stats Bar */}
          <Text
            variant="body"
            {...{ "data-testid": TEST_ID_PLAYER_KANBAN.SIDEBAR_STATS }}
            className="text-content-muted"
          >
            {stats.totalPlayers} players • Avg GPR {stats.avgGpr} • Avg{" "}
            {stats.avgContract}
          </Text>
        </Stack>
        {/* Search & Sort Section */}
        <SearchAndControls
          searchQuery={searchQuery}
          sortLabel="Name"
          onSearchChange={onSearchChange}
          onSortChange={onSortChange}
        />
        {/* Loading State */}
        {isLoadingPlayers && (
          <Box className="flex-1 items-center justify-center p-4">
            <Stack direction="horizontal" className="items-center gap-2">
              <Spinner size="small" />
              <Text variant="body" className="text-content-muted">
                Loading...
              </Text>
            </Stack>
          </Box>
        )}
        {/* Error State */}
        {playersError && !isLoadingPlayers && (
          <Box className="flex-1 items-center justify-center p-4">
            <Stack className="items-center gap-1">
              <Text variant="body" className="text-center text-status-error">
                {playersError}
              </Text>
              <Text variant="caption" className="text-content-subtle">
                Try refreshing
              </Text>
            </Stack>
          </Box>
        )}
        {/* Player List */}
        {!isLoadingPlayers && !playersError && (
          <PlayerListSection
            players={players}
            playersOnKanban={playersOnKanban}
            onPlayerClick={onPlayerClick}
            onGlobalDragStart={onGlobalDragStart}
            onGlobalDragEnd={onGlobalDragEnd}
            onAutoScroll={onAutoScroll}
            onLoadMore={onLoadMore}
          />
        )}
      </Stack>
    )}
  </Box>
);

PlayerSidebarView.displayName = "PlayerSidebarView";

export default memo(PlayerSidebarView);
